import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import GoneNotice from "@/components/seo/GoneNotice";
import { Button } from "@/components/ui/button";
import { ArrowLeft, TrendingUp, History } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL } from "@/constants/seo";
import { CityActivityHeader } from "@/components/voting/CityActivityHeader";
import { LeaderboardTop3 } from "@/components/voting/LeaderboardTop3";
import { NearMissGrid } from "@/components/voting/NearMissGrid";
import { buildPostalAddressStrict } from "@/utils/structuredData/postalAddress";

type ViewMode = "trending" | "alltime";

interface RankingEntry {
  listing_id: string;
  listing_name: string;
  listing_slug: string;
  listing_thumbnail: string;
  listing_description: string;
  listing_address?: string;
  all_time_votes: number;
  trending_score: number;
  last_vote_at: string;
}

const CityActivity = () => {
  const { citySlug, activitySlug } = useParams<{ citySlug: string; activitySlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState<ViewMode>("trending");
  const [cityName, setCityName] = useState("");
  const [activityName, setActivityName] = useState("");
  const [rankings, setRankings] = useState<RankingEntry[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!citySlug || !activitySlug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        // Fetch city and activity info
        const { data: cityData } = await supabase
          .from("cities")
          .select("id, name")
          .eq("slug", citySlug)
          .maybeSingle();

        const { data: activityData } = await supabase
          .from("activities")
          .select("id, name")
          .eq("slug", activitySlug)
          .maybeSingle();

        if (!cityData || !activityData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setCityName(cityData.name);
        setActivityName(activityData.name);

        // Fetch rankings using the view - ALWAYS sort by votes descending for consistent ranking
        const { data: rankingData, error } = await supabase
          .from("v_leaderboard")
          .select(`
            listing_id,
            all_time_votes,
            trending_score,
            last_vote_at,
            listings (
              name,
              slug,
              thumbnail_url,
              short_description,
              address
            )
          `)
          .eq("city_id", cityData.id)
          .eq("activity_id", activityData.id)
          .order("all_time_votes", { ascending: false })
          .limit(10);

        if (error) throw error;

        const formatted: RankingEntry[] = (rankingData || []).map((r: any) => ({
          listing_id: r.listing_id,
          listing_name: r.listings.name,
          listing_slug: r.listings.slug,
          listing_thumbnail: r.listings.thumbnail_url || '/default-listing-fallback.png',
          listing_description: r.listings.short_description,
          listing_address: r.listings.address,
          all_time_votes: r.all_time_votes,
          trending_score: r.trending_score,
          last_vote_at: r.last_vote_at,
        }));


        setRankings(formatted);
      } catch (error) {
        console.error("Error fetching rankings:", error);
        toast.error("Error al cargar el ranking");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [citySlug, activitySlug, mode, navigate]);

  const top3 = rankings.slice(0, 3);
  const nearMiss = rankings.slice(3, 9); // Show exactly 6 runner-ups

  const pageTitle = `Top 10 Mejores ${activityName} en ${cityName} ${new Date().getFullYear()} | Ranking Comunitario`;
  const pageDescription = `🏆 Descubre los ${rankings.length} mejores centros de ${activityName} en ${cityName} según ${rankings.reduce((sum, r) => sum + r.all_time_votes, 0)}+ votos reales de la comunidad. Ranking actualizado en tiempo real con opiniones verificadas, precios y ubicaciones exactas.`;
  
  // JSON-LD structured data for rankings
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": pageTitle,
      "description": pageDescription,
      "url": `${SITE_URL}/${citySlug}/${activitySlug}`,
      "about": {
        "@type": "SportsActivityLocation",
        "name": activityName,
        ...(buildPostalAddressStrict({
          streetAddress: null,
          addressLocality: cityName,
          postalCode: null,
          addressRegion: null,
          addressCountry: "ES",
        })
          ? {
              address: buildPostalAddressStrict({
                streetAddress: null,
                addressLocality: cityName,
                postalCode: null,
                addressRegion: null,
                addressCountry: "ES",
              }),
            }
          : {})
      },
      "mainEntity": {
        "@type": "ItemList",
        "numberOfItems": rankings.length,
        "itemListElement": top3.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "LocalBusiness",
            "name": item.listing_name,
            "url": `${SITE_URL}/listing/${item.listing_slug}`
          }
        }))
      }
    }
  ];

  if (notFound) {
    return (
      <GoneNotice
        title="Ranking no disponible"
        description="No tenemos un ranking activo para esta combinación de ciudad y actividad. Explora las ciudades y actividades disponibles."
        backTo="/explore"
        backLabel="Explorar rankings"
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-32 lg:pt-40 pb-12">
          <div className="container mx-auto px-4 text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonical={`${SITE_URL}/${citySlug}/${activitySlug}`}
        structuredData={structuredData}
        keywords={[activityName, cityName, 'ranking', 'mejores', 'votos', 'comunidad']}
      />
      <Navigation />

      <main className="pt-32 lg:pt-40 pb-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <Breadcrumbs
            items={[
              { name: "Explorar", path: "/explore" },
              { name: `${activityName} en ${cityName}`, path: `/${citySlug}/${activitySlug}` },
            ]}
          />

          <Button
            variant="ghost"
            onClick={() => navigate("/explore")}
            className="mb-6 hover:bg-primary/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Explorar
          </Button>

          <CityActivityHeader
            cityName={cityName}
            activityName={activityName}
            mode={mode}
            onModeChange={setMode}
            totalListings={rankings.length}
          />

          {/* Trust Section */}
          <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span>Votados por la Comunidad</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span>{rankings.length} Centros Verificados</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-purple-500"></div>
              <span>Actualizado en Tiempo Real</span>
            </div>
          </div>

          {rankings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                Aún no hay votos para esta combinación. ¡Sé el primero en votar!
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {top3.length > 0 && (
                <section>
                  <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
                    🏆 Top 3
                  </h2>
                  <LeaderboardTop3
                    items={top3.map((item, index) => ({
                      rank: index + 1,
                      listing_id: item.listing_id,
                      listing_name: item.listing_name,
                      listing_slug: item.listing_slug,
                      listing_thumbnail: item.listing_thumbnail,
                      listing_address: item.listing_address,
                      all_time_votes: item.all_time_votes,
                    }))}
                    citySlug={citySlug!}
                    activitySlug={activitySlug!}
                  />
                </section>
              )}

              {nearMiss.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-6">
                    🎯 Top 4 - 9
                  </h2>
                  <NearMissGrid
                    items={nearMiss.map((item, index) => ({
                      rank: index + 4,
                      listing_id: item.listing_id,
                      listing_name: item.listing_name,
                      listing_slug: item.listing_slug,
                      listing_thumbnail: item.listing_thumbnail,
                      listing_address: item.listing_address,
                      all_time_votes: item.all_time_votes,
                    }))}
                    citySlug={citySlug!}
                    activitySlug={activitySlug!}
                  />
                </section>
              )}

              {/* CTA Conversión */}
              <section className="mt-12 bg-gradient-primary text-white rounded-lg p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">
                  ¿Es Tu Negocio? Destaca Aquí
                </h3>
                <p className="text-lg mb-6 opacity-90">
                  Miles de usuarios buscan centros como el tuyo cada mes en {cityName}
                </p>
                <Button 
                  size="lg"
                  variant="secondary"
                  onClick={() => navigate("/?auth=signup")}
                  className="bg-white text-primary hover:bg-white/90"
                >
                  Registrar Mi Negocio Gratis
                </Button>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CityActivity;
